/**
 * Utility functions for formatting and parsing duration values
 */

/**
 * Formats a duration in hours to a human-readable string.
 * Handles sub-hour values properly by converting to minutes when appropriate.
 *
 * @param hours - Duration in hours (can be decimal, e.g., 0.5 for 30 minutes)
 * @returns Formatted string like "1.5 h", "30 min", or "0 h" for zero/null values
 */
export function formatDuration(hours: number | null | undefined): string {
  if (hours == null || hours === 0) {
    return '0 h';
  }

  // If less than 1 hour, show in minutes for clarity
  if (hours < 1) {
    const minutes = Math.round(hours * 60);
    return `${minutes} min`;
  }

  // For 1 hour or more, show in hours
  // Remove unnecessary decimal places (e.g., 2.0 -> 2, 1.5 -> 1.5)
  const formattedHours = hours % 1 === 0 ? Math.round(hours) : hours;
  return `${formattedHours} h`;
}

/**
 * Sums decimal-hour values numerically. The backend `numeric` columns can reach
 * the client as strings (e.g. "0.50"); coerce each value to a number first so
 * they add instead of string-concatenating (#1021). Non-numeric / null values
 * count as 0, and the total is rounded to 2 decimals (the DB scale) to avoid
 * floating-point artefacts such as 0.1 + 0.2 = 0.30000000000000004.
 */
export function sumHours(values: ReadonlyArray<number | string | null | undefined>): number {
  const total = values.reduce<number>((sum, value) => {
    const n = typeof value === 'number' ? value : Number(value);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);
  return Math.round(total * 100) / 100;
}

/**
 * Parses a user-entered duration string into a positive number of hours.
 * Accepts both comma and dot as decimal separator (Finnish `1,5` and English `1.5`).
 * Returns null for empty, non-numeric, or non-positive input.
 */
export function parseDurationHours(value: string): number | null {
  if (value == null) return null;
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const normalized = trimmed.replace(',', '.');
  const parsed = Number.parseFloat(normalized);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
}
/** Duration step when the workshop has never chosen one (#3091). */
export const DEFAULT_WORK_DURATION_STEP_MINUTES = 30;

/**
 * Moves `hours` one step up or down on the workshop's duration grid (#3091).
 * An off-grid value (typed by hand, or summed from services) snaps to the
 * neighbouring grid point first, so 1.2 h with a 30 min step goes to 1.5 h
 * up and 1 h down — never 1.7 h. Never goes below 0.
 */
export function stepDurationHours(hours: number, stepMinutes: number, direction: 1 | -1): number {
  const stepHours = stepMinutes / 60;
  // Round away float noise before deciding whether the value is on the grid.
  const units = Math.round((hours / stepHours) * 1e6) / 1e6;
  const next = direction === 1 ? Math.floor(units) + 1 : Math.ceil(units) - 1;
  return Math.max(0, Math.round(next * stepHours * 1e4) / 1e4);
}
